import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { signOut } from '../../lib/auth';

interface HeaderProps {
  onLoginClick: () => void;
}

export function Header({ onLoginClick }: HeaderProps) {
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/rules', label: t('nav.rules') },
    { to: '/schedule', label: t('nav.schedule') },
    { to: '/scoreboard', label: t('nav.scoreboard') },
    { to: '/news', label: t('nav.news') },
    { to: '/links', label: t('nav.links') },
  ];

  return (
    <header className="bg-mafia-black/95 backdrop-blur-sm sticky top-0 z-50 border-b border-mafia-border">
      {/* Gold accent hairline at top */}
      <div className="h-px bg-gradient-to-r from-transparent via-mafia-gold/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/logo.jpg"
              alt="Queensland Mafia Club"
              className="h-9 w-9 rounded-full transition-opacity group-hover:opacity-90"
            />
            <span className="font-display text-base font-bold text-mafia-gold tracking-wide hidden sm:block group-hover:text-mafia-gold-light transition-colors">
              Queensland Mafia Club
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-5">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `text-xs font-mono tracking-widest uppercase transition-colors relative pb-px ${
                    isActive
                      ? 'text-mafia-gold border-b border-mafia-gold'
                      : 'text-mafia-paper-dim hover:text-mafia-gold'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Auth actions */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/reserve" className="btn-gold hidden sm:inline-flex items-center">
                  {t('nav.reserve')}
                </Link>
                <Link
                  to="/profile"
                  className="hidden sm:block text-mafia-paper-dim hover:text-mafia-gold text-xs font-mono tracking-wider transition-colors max-w-[120px] truncate"
                  title={user.displayName}
                >
                  {user.displayName}
                </Link>
                <button
                  onClick={() => signOut()}
                  className="text-mafia-muted hover:text-mafia-paper text-xs font-mono tracking-wider uppercase transition-colors"
                >
                  {t('nav.signOut')}
                </button>
              </>
            ) : (
              <button onClick={onLoginClick} className="btn-gold">
                {t('nav.signIn')}
              </button>
            )}

            {/* Burger */}
            <button
              className="md:hidden text-mafia-paper-dim hover:text-mafia-gold transition-colors ml-1"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Меню"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {menuOpen && (
          <nav className="md:hidden border-t border-mafia-border pb-4 pt-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `text-xs font-mono tracking-widest uppercase py-2.5 px-2 transition-colors ${
                    isActive ? 'text-mafia-gold' : 'text-mafia-paper-dim'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            {user && (
              <>
                <Link
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                  className="text-xs font-mono tracking-widest uppercase py-2.5 px-2 text-mafia-paper-dim hover:text-mafia-gold transition-colors"
                >
                  {user.displayName}
                </Link>
                <Link
                  to="/reserve"
                  onClick={() => setMenuOpen(false)}
                  className="mt-2 btn-gold-solid text-center"
                >
                  {t('nav.reserve')}
                </Link>
              </>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
